import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import type { Party, PartyCreateInput, PartyUpdateInput } from 'shared/schemas';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import { Button } from '../../../components/ui/button.tsx';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog.tsx';
import { FilterBar } from '../../../components/layout/FilterBar.tsx';
import { GradientCard } from '../../../components/layout/GradientCard.tsx';
import { KpiCard } from '../../../features/dashboard/components/KpiCard.tsx';
import { KpiStrip } from '../../../features/dashboard/components/KpiStrip.tsx';
import { MasterDetail } from '../../../components/layout/MasterDetail.tsx';
import { EmptyStateV3 } from '../../../components/layout/EmptyStateV3.tsx';
import { PageHeader } from '../../../components/layout/PageHeader.tsx';
import { PartyDeleteDialog } from '../../../features/parties/components/PartyDeleteDialog.tsx';
import { PartyDetailPane } from '../../../features/parties/components/PartyDetailPane.tsx';
import { PartyForm } from '../../../features/parties/components/PartyForm.tsx';
import { useCreateParty } from '../../../features/parties/hooks/useCreateParty.ts';
import { useParties } from '../../../features/parties/hooks/useParties.ts';
import { usePartiesStats } from '../../../features/parties/hooks/usePartiesStats.ts';
import { useUpdateParty } from '../../../features/parties/hooks/useUpdateParty.ts';
import { STRINGS } from '../../../features/parties/parties.strings.ts';

const partiesSearchSchema = z.object({
  view: z.string().optional(),
  edit: z.string().optional(),
  new: z.boolean().optional(),
});

export const Route = createFileRoute('/_authed/parties/')({
  validateSearch: (s) => partiesSearchSchema.parse(s),
  component: PartiesPage,
});

function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function PartiesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('todos');
  const [partyToDelete, setPartyToDelete] = useState<Party | null>(null);

  const { view: selectedId, new: isNewOpen, edit: editId } = Route.useSearch();

  const kind =
    activeTab === 'externos' ? 'external' : activeTab === 'propios' ? 'self' : undefined;

  const { data, isLoading } = useParties({
    page: 1,
    search: search || undefined,
    kind,
  });

  const stats = usePartiesStats();
  const createParty = useCreateParty();
  const updateParty = useUpdateParty(editId ?? '');

  const tabs = [
    { id: 'todos', label: STRINGS.KIND_ALL, count: stats.total },
    { id: 'externos', label: STRINGS.KIND_EXTERNAL, count: stats.externos },
    { id: 'propios', label: STRINGS.KIND_SELF, count: stats.propios },
  ];

  const parties = data?.data ?? [];
  const selectedParty = parties.find((p) => p.id === selectedId) ?? null;

  function openNew() {
    void navigate({ to: '/parties', search: { new: true }, replace: true });
  }

  function closeModals() {
    void navigate({ to: '/parties', search: {}, replace: true });
  }

  function selectParty(party: Party) {
    void navigate({
      to: '/parties',
      search: { view: party.id },
      replace: true,
    });
  }

  function handleCreate(data: PartyCreateInput) {
    createParty.mutate(data, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_CREATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  function handleUpdate(data: PartyUpdateInput) {
    updateParty.mutate(data, {
      onSuccess: () => {
        toast.success(STRINGS.TOAST_UPDATE_SUCCESS);
        closeModals();
      },
      onError: () => {
        toast.error(STRINGS.TOAST_ERROR_GENERIC);
      },
    });
  }

  const editParty = editId ? parties.find((p) => p.id === editId) : null;

  const masterPane = (
    <div className="flex h-full flex-col rounded-lg border border-border bg-card">
      {/* Search */}
      <div className="border-b border-border px-3 py-2">
        <FilterBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={(id) => setActiveTab(id)}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder={STRINGS.PLACEHOLDER_SEARCH}
          compact
        />
      </div>

      {/* Party list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-4 text-sm text-muted-foreground">Cargando...</div>
        ) : parties.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {STRINGS.EMPTY_STATE}
          </div>
        ) : (
          <ul>
            {parties.map((party) => {
              const isSelected = party.id === selectedId;
              return (
                <li key={party.id}>
                  <button
                    type="button"
                    data-testid="party-list-item"
                    onClick={() => selectParty(party)}
                    className={`flex w-full items-center gap-3 border-l-2 px-4 py-3 text-left transition-colors hover:bg-muted/50 ${
                      isSelected
                        ? 'border-l-primary bg-primary/5'
                        : 'border-l-transparent'
                    }`}
                  >
                    <Avatar className="size-9 shrink-0">
                      <AvatarFallback className="text-xs">
                        {getInitials(party.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{party.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {party.contactEmail ?? party.contactPhone ?? '—'}
                      </p>
                    </div>
                    <Badge
                      variant={party.kind === 'self' ? 'default' : 'secondary'}
                      className="shrink-0 text-xs"
                    >
                      {party.kind === 'self' ? STRINGS.KIND_SELF : STRINGS.KIND_EXTERNAL}
                    </Badge>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );

  const detailPane = selectedParty ? (
    <PartyDetailPane
      party={selectedParty}
      onEdit={(p) =>
        void navigate({
          to: '/parties',
          search: { edit: p.id },
          replace: true,
        })
      }
      onDelete={(p) => setPartyToDelete(p)}
    />
  ) : null;

  const emptyDetail = (
    <GradientCard className="flex h-full items-center justify-center p-8">
      <EmptyStateV3
        icon={UserPlus}
        title="Selecciona un contacto"
        description="Elige un contacto de la lista para ver su detalle."
      />
    </GradientCard>
  );

  return (
    <div className="flex-1 overflow-auto px-3 sm:px-6 py-4 sm:py-6">
      <PageHeader
        title={STRINGS.LIST_TITLE}
        actions={
          <Button type="button" onClick={openNew}>
            {STRINGS.NEW_BUTTON}
          </Button>
        }
      />

      <KpiStrip>
        <KpiCard label={STRINGS.KIND_ALL} value={String(stats.total)} />
        <KpiCard label={STRINGS.KIND_EXTERNAL} value={String(stats.externos)} />
        <KpiCard label={STRINGS.KIND_SELF} value={String(stats.propios)} />
        <KpiCard label="Con email" value={String(stats.fourth.value)} />
      </KpiStrip>

      <div className="mt-6">
        <MasterDetail
          master={masterPane}
          detail={detailPane}
          emptyDetail={emptyDetail}
          className="min-h-[600px]"
        />
      </div>

      {/* New party dialog */}
      <Dialog open={isNewOpen === true} onOpenChange={(open) => { if (!open) closeModals(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{STRINGS.NEW_BUTTON}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <PartyForm
              mode="create"
              formId="party-create-form"
              onSubmit={handleCreate}
              isPending={createParty.isPending}
            />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModals}>
              {STRINGS.ACTION_CANCEL}
            </Button>
            <Button type="submit" form="party-create-form" disabled={createParty.isPending}>
              {STRINGS.ACTION_SAVE}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit party dialog */}
      <Dialog open={editParty !== null && editParty !== undefined} onOpenChange={(open) => { if (!open) closeModals(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{STRINGS.EDIT_TITLE}</DialogTitle>
          </DialogHeader>
          {editParty && (
            <>
              <DialogBody>
                <PartyForm
                  mode="edit"
                  formId="party-edit-form"
                  initialValues={editParty}
                  onSubmit={handleUpdate}
                  isPending={updateParty.isPending}
                />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeModals}>
                  {STRINGS.ACTION_CANCEL}
                </Button>
                <Button type="submit" form="party-edit-form" disabled={updateParty.isPending}>
                  {STRINGS.ACTION_SAVE}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <PartyDeleteDialog
        party={partyToDelete}
        open={partyToDelete !== null}
        onClose={() => setPartyToDelete(null)}
      />
    </div>
  );
}
